/**
 * Background Location Service – Clean rebuild.
 *
 * iOS native only (Transistorsoft Capacitor Background Geolocation).
 * No web/PWA fallback.
 *
 * Two modes:
 *  1. FOREGROUND-ONLY (default): Plugin runs while app is open.
 *     Stopped when app goes to background. No battery drain.
 *  2. CLOCKED-IN: Plugin runs always (foreground + background + after kill).
 *     Activated by clock-in button, deactivated by clock-out button.
 *
 * API:
 *  - initPlugin(config)    – call ONCE on app start (ready + listeners)
 *  - startTracking()       – turn on GPS + HTTP uploads
 *  - stopTracking()        – turn off GPS + flush stale queue
 *  - getPosition()         – one-off GPS read (for own-dot display)
 *  - setClockedIn(bool)    – toggle clocked-in mode (persisted)
 *  - isClockedIn()         – read the flag
 *  - isTracking()          – is the plugin currently running?
 *  - ensureTracking()      – verify plugin is alive and restart if stuck
 *
 * IMPORTANT: ready() is called exactly ONCE in initPlugin(). start()/stop()
 * toggle tracking on and off. Never call ready() again.
 */

const LOG = '📍 BG-LOC:'

/**
 * Resolve `promise` with `fallback` if it doesn't settle within `ms`.
 * Used so a slow GPS fix never blocks clock-in/out (the UI would otherwise
 * appear frozen — button greyed, timer still running — until GPS responds).
 */
function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      if (!settled) { settled = true; resolve(fallback) }
    }, ms)
    promise.then(
      (val) => { if (!settled) { settled = true; clearTimeout(timer); resolve(val) } },
      () => { if (!settled) { settled = true; clearTimeout(timer); resolve(fallback) } },
    )
  })
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

const LOCATION_URL = SUPABASE_URL
  ? `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/update-resource-location`
  : ''

const LOCATION_TEMPLATE =
  '{"lat":<%= latitude %>,"lng":<%= longitude %>,"accuracy":<%= accuracy %>,"heading":<%= heading %>,"speed":<%= speed %>,"gps_timestamp":"<%= timestamp %>","activity":"<%= activity.type %>","activity_confidence":<%= activity.confidence %>,"odometer":<%= odometer %>}'

const CLOCKED_IN_KEY = 'bg_location_clocked_in'

// ── Module state ──────────────────────────────────────────
export interface BackgroundLocationConfig {
  businessId: string
  accessToken: string
}

let _initialized = false   // ready() has been called
let _tracking = false       // plugin is currently started
let _clockedIn = false      // persisted clocked-in flag

let _currentActivity = 'unknown'
let _currentActivityConfidence = 0
let _businessId = ''
let _accessToken = ''
let _providerChangeCallback: ((permission: string) => void) | null = null

// ── Public API ────────────────────────────────────────────

/**
 * Initialize the Transistorsoft plugin. Call ONCE when user signs in.
 * Sets up ready(), event listeners, and HTTP config.
 * Does NOT start tracking – call startTracking() after this.
 */
export async function initPlugin(config: BackgroundLocationConfig): Promise<boolean> {
  if (_initialized) {
    console.log(LOG, '[init] Already initialized, skipping')
    return true
  }

  if (!LOCATION_URL || !SUPABASE_ANON_KEY) {
    console.warn(LOG, '[init] Missing SUPABASE env vars')
    return false
  }

  const { Capacitor } = await import('@capacitor/core')
  if (!Capacitor.isNativePlatform()) {
    console.log(LOG, '[init] Not native – skipping')
    return false
  }

  try {
    const module = await import('@transistorsoft/capacitor-background-geolocation')
    const BG = module.default
    if (!BG || typeof BG.ready !== 'function') {
      console.warn(LOG, '[init] Plugin not available')
      return false
    }

    // ── Event listeners (before ready) ──
    BG.onLocation(
      (loc: any) => {
        console.log(LOG, '[onLocation]', {
          lat: loc?.coords?.latitude?.toFixed(5),
          lng: loc?.coords?.longitude?.toFixed(5),
          accuracy: loc?.coords?.accuracy,
          speed: loc?.coords?.speed,
          isMoving: loc?.is_moving,
        })
      },
      (err: any) => console.error(LOG, '[onLocation] ERROR:', err)
    )

    BG.onHttp((res: any) => {
      console.log(LOG, '[onHttp]', { status: res?.status, success: res?.success })
    })

    BG.onActivityChange((event: any) => {
      _currentActivity = event?.activity || 'unknown'
      _currentActivityConfidence = event?.confidence ?? 0
      console.log(LOG, '[activity]', _currentActivity, _currentActivityConfidence)
      BG.setConfig({
        extras: {
          business_id: _businessId,
          activity: _currentActivity,
          activity_confidence: _currentActivityConfidence,
        },
      }).catch(() => {})
    })

    BG.onHeartbeat(() => {
      console.log(LOG, '[heartbeat] Requesting fresh position')
      BG.getCurrentPosition({ samples: 1, persist: true }).catch(() => {})
    })

    BG.onEnabledChange((enabled: boolean) => {
      console.log(LOG, '[enabledChange]', enabled)
      _tracking = enabled
    })

    BG.onProviderChange((event: any) => {
      const permission = _mapAuthorizationStatus(event?.status)
      console.log(LOG, '[providerChange]', { status: event?.status, permission, enabled: event?.enabled })
      if (_providerChangeCallback) {
        _providerChangeCallback(permission)
      }
    })

    // ── ready() – ONCE ──
    // Restore clocked-in flag BEFORE ready() so we can set the right defaults
    _loadClockedIn()
    const clockedIn = _clockedIn
    _businessId = config.businessId
    _accessToken = config.accessToken

    console.log(LOG, '[init] Calling ready()… clockedIn:', clockedIn)
    const state = await BG.ready({
      // GPS — 10m filter gives frequent updates while moving
      distanceFilter: 10,
      desiredAccuracy: BG.DESIRED_ACCURACY_HIGH ?? -1,
      // Background behavior — only persist through app kill when clocked in
      stopOnTerminate: !clockedIn,
      startOnBoot: clockedIn,
      // HTTP uploads to edge function
      url: LOCATION_URL,
      method: 'POST' as const,
      headers: {
        Authorization: `Bearer ${config.accessToken}`,
        apikey: SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
        'X-Business-Id': config.businessId,
      },
      extras: {
        business_id: config.businessId,
        activity: _currentActivity,
        activity_confidence: _currentActivityConfidence,
      },
      httpRootProperty: '.',
      locationTemplate: LOCATION_TEMPLATE,
      autoSync: true,
      autoSyncThreshold: 0,
      batchSync: true,
      maxBatchSize: 250,
      // Android
      foregroundService: true,
      notification: {
        title: 'Sharing location',
        text: 'Location is being shared with your team',
      },
      // iOS — let stop detection work so stationary mode saves battery
      locationAuthorizationRequest: 'Always',
      disableStopDetection: false,
      stopTimeout: 3,
      pausesLocationUpdatesAutomatically: false,
      heartbeatInterval: 120,
      // Debug
      debug: false,
      logLevel: 5,
    })

    // ready() may restore persisted config from a previous session, overwriting
    // the extras/headers we just passed. Force-set them again to guarantee the
    // current business_id and JWT are used for HTTP uploads.
    try {
      await BG.setConfig({
        url: LOCATION_URL,
        headers: {
          Authorization: `Bearer ${config.accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
          'X-Business-Id': config.businessId,
        },
        extras: {
          business_id: config.businessId,
          activity: _currentActivity,
          activity_confidence: _currentActivityConfidence,
        },
      })
      console.log(LOG, '[init] Force-set config after ready() — business_id:', config.businessId)
    } catch (e) {
      console.warn(LOG, '[init] Could not force-set config (non-fatal):', e)
    }

    // CRITICAL: Flush any stale queued positions IMMEDIATELY after ready().
    // The plugin may have been running in the background (stopOnTerminate: false)
    // and accumulated old GPS readings in its SQLite queue. If autoSync uploads
    // those before we clear them, the database gets hour-old data with fresh
    // timestamps, making the map show ghost replays of old drives.
    try {
      await BG.destroyLocations()
      console.log(LOG, '[init] Stale queue flushed after ready()')
    } catch {
      console.warn(LOG, '[init] Could not flush queue (non-fatal)')
    }

    _initialized = true
    _tracking = !!state?.enabled

    console.log(LOG, '[init] ready() done:', {
      enabled: state?.enabled,
      isMoving: state?.isMoving,
      distanceFilter: state?.distanceFilter,
      stopOnTerminate: !clockedIn,
      startOnBoot: clockedIn,
    })

    return true
  } catch (e) {
    console.error(LOG, '[init] EXCEPTION:', e)
    return false
  }
}

/**
 * Start tracking. Flushes any stale queued positions first so only
 * fresh GPS data gets uploaded.
 */
export async function startTracking(): Promise<void> {
  if (!_initialized) {
    console.warn(LOG, '[start] Not initialized yet')
    return
  }

  try {
    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default

    // Check actual plugin state — our _tracking flag may be stale if the OS
    // killed the plugin or it got into an unexpected state.
    let pluginState: any = null
    try {
      pluginState = await BG.getState()
    } catch {}
    const pluginEnabled = !!pluginState?.enabled

    if (_tracking && pluginEnabled) {
      console.log(LOG, '[start] Already tracking (confirmed by plugin state)')
      // Even if already tracking, kick to moving mode to ensure updates flow
      try { await BG.changePace(true) } catch {}
      return
    }

    if (_tracking && !pluginEnabled) {
      console.warn(LOG, '[start] _tracking was true but plugin reports disabled — restarting')
      _tracking = false
    }

    // Flush any stale positions from the plugin's internal SQLite queue.
    try {
      await BG.destroyLocations()
      console.log(LOG, '[start] Stale queue flushed')
    } catch {}

    await BG.start()
    _tracking = true
    console.log(LOG, '[start] Plugin started')

    // Force moving mode so we get updates immediately
    try { await BG.changePace(true) } catch {}

    // Grab a fresh position right now so the first upload is accurate
    try {
      const loc = await BG.getCurrentPosition({ samples: 1, persist: true })
      console.log(LOG, '[start] Fresh position:', {
        lat: loc?.coords?.latitude?.toFixed(5),
        lng: loc?.coords?.longitude?.toFixed(5),
        accuracy: loc?.coords?.accuracy,
      })
    } catch {}
  } catch (e) {
    console.error(LOG, '[start] EXCEPTION:', e)
  }
}

/**
 * Health check: verify the plugin is actually running and restart if stuck.
 * Call periodically (e.g. every 60s) to recover from OS kills, stuck states,
 * or init/start mismatches.
 *
 * Returns true if tracking is healthy after the check.
 */
export async function ensureTracking(): Promise<boolean> {
  if (!_initialized) {
    console.warn(LOG, '[ensureTracking] Plugin not initialized')
    return false
  }

  try {
    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
    const state = await BG.getState()
    const pluginEnabled = !!state?.enabled

    console.log(LOG, '[ensureTracking] State check:', {
      _tracking,
      pluginEnabled,
      isMoving: state?.isMoving,
      schedulerEnabled: state?.schedulerEnabled,
    })

    if (!pluginEnabled) {
      console.warn(LOG, '[ensureTracking] Plugin disabled — restarting')
      _tracking = false
      await startTracking()
      return _tracking
    }

    // Plugin says it's enabled but might be stuck in stationary mode
    if (!state?.isMoving) {
      console.log(LOG, '[ensureTracking] Plugin stationary — kicking to moving mode')
      try { await BG.changePace(true) } catch {}
    }

    _tracking = true
    return true
  } catch (e) {
    console.error(LOG, '[ensureTracking] EXCEPTION:', e)
    return false
  }
}

/**
 * Stop tracking. Flushes the queue so stale data doesn't get
 * uploaded when tracking resumes.
 */
export async function stopTracking(): Promise<void> {
  if (!_initialized || !_tracking) return

  try {
    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
    await BG.stop()
    _tracking = false
    console.log(LOG, '[stop] Plugin stopped')

    // Flush the queue so stale positions aren't uploaded on next start
    try {
      await BG.destroyLocations()
      console.log(LOG, '[stop] Queue flushed')
    } catch {}
  } catch (e) {
    console.error(LOG, '[stop] EXCEPTION:', e)
  }
}

/**
 * One-off GPS read for displaying the user's own dot on the map.
 * persist = false for admins (no DB write), true for resources.
 */
export async function getPosition(persist: boolean = false): Promise<{ lat: number; lng: number } | null> {
  if (!_initialized) return null

  try {
    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) return null

    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
    // Time-box the GPS read (plugin `timeout` is in seconds) AND race it at the JS
    // level so a slow/absent fix can never block the caller (e.g. clock-out).
    const loc = await withTimeout(
      BG.getCurrentPosition({ samples: 1, persist, timeout: 5 }),
      5000,
      null as any,
    )
    if (loc?.coords) {
      return { lat: loc.coords.latitude, lng: loc.coords.longitude }
    }
  } catch (e) {
    console.warn(LOG, '[getPosition]', e)
  }
  return null
}

/** Is the plugin currently tracking? */
export function isTracking(): boolean {
  return _tracking
}

/** Has the plugin been initialized? */
export function isInitialized(): boolean {
  return _initialized
}

/**
 * Update the HTTP Authorization header with a fresh JWT.
 * Called by the auth state change listener when Supabase refreshes the token.
 * Without this, the plugin silently fails with 401 after ~1 hour.
 *
 * After updating headers we flush any queued (failed-401) locations via sync()
 * and grab a fresh GPS reading so the edge function receives up-to-date data.
 */
export async function updateAuthToken(newAccessToken: string): Promise<void> {
  if (!_initialized) return
  _accessToken = newAccessToken

  try {
    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
    await BG.setConfig({
      headers: {
        Authorization: `Bearer ${newAccessToken}`,
        apikey: SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
        'X-Business-Id': _businessId,
      },
      extras: {
        business_id: _businessId,
        activity: _currentActivity,
        activity_confidence: _currentActivityConfidence,
      },
    })
    console.log(LOG, '[updateAuthToken] HTTP headers + extras refreshed, business_id:', _businessId)

    // Retry any queued locations that failed while the old token was expired
    try { await BG.sync() } catch {}

    // Kick the plugin out of stationary mode and grab a fresh position
    try { await BG.changePace(true) } catch {}
    try { await BG.getCurrentPosition({ samples: 1, persist: true }) } catch {}
  } catch (e) {
    console.error(LOG, '[updateAuthToken] EXCEPTION:', e)
  }
}

// ── Clocked-in mode ───────────────────────────────────────

/** Set clocked-in mode (called by clock-in/out buttons) */
export async function setClockedIn(value: boolean): Promise<void> {
  _clockedIn = value
  try { localStorage.setItem(CLOCKED_IN_KEY, String(value)) } catch {}
  console.log(LOG, '[setClockedIn]', value)

  if (_initialized) {
    try {
      const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
      await BG.setConfig({
        stopOnTerminate: !value,
        startOnBoot: value,
      })
      console.log(LOG, '[setClockedIn] Config updated — stopOnTerminate:', !value, 'startOnBoot:', value)
    } catch (e) {
      console.warn(LOG, '[setClockedIn] Could not update config:', e)
    }
  }

  if (value) {
    // Zero the odometer at the start of every shift so the final reading
    // on clock-out equals the total distance for this shift.
    if (_initialized) {
      try {
        const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
        await BG.resetOdometer()
        console.log(LOG, '[setClockedIn] Odometer reset to 0')
      } catch (e) {
        console.warn(LOG, '[setClockedIn] Could not reset odometer:', e)
      }
    }

    if (!_tracking) {
      await startTracking()
    } else {
      // Already tracking — force moving mode + fresh position so the edge
      // function gets an immediate update after clock-in.
      try {
        const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
        try { await BG.changePace(true) } catch {}
        try { await BG.getCurrentPosition({ samples: 1, persist: true }) } catch {}
        console.log(LOG, '[setClockedIn] Forced fresh position (already tracking)')
      } catch {}
    }
  }
}

/** Is the user currently clocked in? */
export function isClockedIn(): boolean {
  return _clockedIn
}

/** Read the current odometer value in kilometers. Returns null on non-native or if not initialized. */
export async function getOdometer(): Promise<number | null> {
  if (!_initialized) return null
  try {
    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
    const meters = await withTimeout(BG.getOdometer(), 4000, null as any)
    if (meters == null) return null
    const km = Math.round((meters / 1000) * 1000) / 1000 // 3 decimal places
    console.log(LOG, '[getOdometer]', meters, 'm =>', km, 'km')
    return km
  } catch (e) {
    console.warn(LOG, '[getOdometer] failed:', e)
    return null
  }
}

/** Restore from localStorage (called during init) */
function _loadClockedIn(): void {
  try {
    _clockedIn = localStorage.getItem(CLOCKED_IN_KEY) === 'true'
    console.log(LOG, '[loadClockedIn]', _clockedIn)
  } catch {
    _clockedIn = false
  }
}

// ── Location permission reporting ─────────────────────────

function _mapAuthorizationStatus(status: number | undefined): string {
  switch (status) {
    case 0: return 'not_determined'
    case 1: return 'restricted'
    case 2: return 'denied'
    case 3: return 'always'
    case 4: return 'when_in_use'
    default: return 'not_determined'
  }
}

/** Register a callback fired whenever the device's location permission changes. */
export function onProviderChange(cb: (permission: string) => void): void {
  _providerChangeCallback = cb
}

/** Read the current provider state. Returns null if plugin isn't initialized. */
export async function getLocationPermission(): Promise<string | null> {
  if (!_initialized) return null
  try {
    const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
    const state = await BG.getProviderState()
    return _mapAuthorizationStatus(state?.status)
  } catch (e) {
    console.warn(LOG, '[getLocationPermission]', e)
    return null
  }
}
